import { useCallback, useMemo } from "react";

import { createContext } from "../../utils/createContext";
import { useStackById, useStackStore } from "./store";

import type { ParamsOf, RoutesOf, ScreensConfig, TypedNavigationActions } from "./types";

/**
 * Context provides the stackId to child components.
 * This allows screens to call useStackNavigation() without knowing which stack they're in.
 */
export const [StackNavigationProvider, useStackId] = createContext<string>({
  name: "StackNavigationContext",
  strict: true,
});

/**
 * Hook to access navigation actions within a StackNavigator.
 * Automatically uses the correct stack from context.
 *
 * **Type Safety:** Pass your screens config as a generic parameter for type-safe navigation.
 *
 * **Performance:** Actions are stable references created via useCallback.
 * The only reactive value is `canPop`, which updates when stack depth changes.
 *
 * @example
 * // Untyped usage (quick and simple)
 * function MyScreen() {
 *   const { push, pop, canPop } = useStackNavigation();
 *   return <Button onPress={() => push("details", { id: 123 })}>View</Button>;
 * }
 *
 * @example
 * // Typed usage (recommended for type safety)
 * const screens = {
 *   "home": { component: HomeScreen },
 *   "details": { component: DetailsScreen }, // DetailsScreen has props: { id: string }
 * } as const satisfies ScreensConfig;
 *
 * function HomeScreen() {
 *   const { push } = useStackNavigation<typeof screens>();
 *   push("details", { id: "123" }); // ✅ typed!
 *   push("details"); // ❌ error: missing id
 * }
 */
export function useStackNavigation<
  T extends ScreensConfig = ScreensConfig,
>(): TypedNavigationActions<T> {
  const stackId = useStackId();

  // Get store actions (stable references from zustand)
  const storePush = useStackStore(state => state.push);
  const storePop = useStackStore(state => state.pop);
  const storeReplace = useStackStore(state => state.replace);
  const storeReset = useStackStore(state => state.reset);
  const storePopTo = useStackStore(state => state.popTo);

  // Get reactive state for this stack
  const { canPop } = useStackById(stackId);

  // Create bound actions for this specific stack
  const push = useCallback(
    <K extends RoutesOf<T>>(route: K, params?: ParamsOf<T, K>) =>
      storePush(stackId, route, params as Record<string, unknown>),
    [storePush, stackId]
  );

  const pop = useCallback(() => storePop(stackId), [storePop, stackId]);

  const replace = useCallback(
    <K extends RoutesOf<T>>(route: K, params?: ParamsOf<T, K>) =>
      storeReplace(stackId, route, params as Record<string, unknown>),
    [storeReplace, stackId]
  );

  const reset = useCallback(
    <K extends RoutesOf<T>>(route: K, params?: ParamsOf<T, K>) =>
      storeReset(stackId, route, params as Record<string, unknown>),
    [storeReset, stackId]
  );

  const popTo = useCallback(
    <K extends RoutesOf<T>>(route: K) => storePopTo(stackId, route),
    [storePopTo, stackId]
  );

  return useMemo(
    (): TypedNavigationActions<T> => ({ push, pop, replace, reset, popTo, canPop }),
    [push, pop, replace, reset, popTo, canPop]
  );
}
